import type React from 'react';
import { memo, type RefObject, useMemo } from 'react';
import { cn } from '../../common/functions/cn';
import { sanitizeHtml } from '../../common/functions/sanitize-html';

interface InnerHTMLDivProps extends React.HTMLAttributes<HTMLDivElement> {
  html: string;
  ref?: RefObject<HTMLDivElement | null>;
  /** When true (default) the html is sanitised with DOMPurify before
   *  insertion.  Set to false when the caller has already applied custom
   *  sanitisation to avoid double-processing. */
  sanitize?: boolean;
}

/**
 * Renders HTML via dangerouslySetInnerHTML with a memoised payload.
 *
 * React 19 re-applies dangerouslySetInnerHTML whenever the prop's object reference
 * changes. Writing `{{ __html: html }}` inline creates a new object every render, so
 * the DOM subtree is rebuilt each time — with service-worker-resolved images that
 * triggered a paint loop (MYOSH-12038). Memoising the object so it changes only when
 * the html or sanitize flag does keeps the subtree stable between unrelated parent
 * re-renders.
 */
const InnerHTMLDiv = memo(function InnerHTMLDiv({ html, className, sanitize = true, ...props }: InnerHTMLDivProps) {
  const dangerousHtml = useMemo(() => ({ __html: sanitize ? sanitizeHtml(html) : html }), [html, sanitize]);

  return (
    // biome-ignore lint/security/noDangerouslySetInnerHtml: payload is sanitised through sanitizeHtml above
    <div className={cn('rich-text', className)} dangerouslySetInnerHTML={dangerousHtml} {...props} />
  );
});

export default InnerHTMLDiv;
